
import { ContentService } from "./content.service.js";
import { HistoryService } from "./history.service.js";
import { getFirestore, doc, getDoc, collection, getDocs, query, where, limit } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const RecommendationService = {
    /**
     * Calculate User's Taste Profile (Tag-based Weighting)
     * Weights: Rating (5) > Like (3) > View (1)
     */
    calculateTasteProfile: async (userId) => {
        // [FIX] Robust Error Handling: Initialize Safe Defaults
        const profile = {
            tagScores: {},
            authorScores: {},
            viewedSeriesIds: new Set()
        };

        if (!userId) return profile;

        console.time("TasteProfile");

        // Use Promise.allSettled to prevent one failure from killing the entire profile
        // e.g. Permission error on 'likes' shouldn't stop history-based exclusion
        try {
            const results = await Promise.allSettled([
                // 1. History
                HistoryService.getHistory(userId, 100),
                // 2. Likes
                getDocs(collection(db, `users/${userId}/likes`)),
                // 3. Ratings
                getDocs(collection(db, `users/${userId}/ratings`)),
                // 4. [NEW] Comments (High Engagement)
                // Assuming 'comments' is a root collection with userId. Auto-index usually handles simple equality.
                getDocs(query(collection(db, 'comments'), where('userId', '==', userId), limit(50)))
            ]);

            // [1] Process History
            const historyResult = results[0];
            if (historyResult.status === 'fulfilled' && historyResult.value && historyResult.value.items) {
                historyResult.value.items.forEach(item => profile.viewedSeriesIds.add(item.seriesId));
            } else {
                console.warn("[Rec] History fetch failed or empty:", historyResult.reason);
            }

            const likedSeriesIds = [];
            const ratedSeries = [];
            const commentedSeriesIds = []; // [NEW]

            // [2] Process Likes
            const likesResult = results[1];
            if (likesResult.status === 'fulfilled') {
                likesResult.value.forEach(doc => {
                    likedSeriesIds.push(doc.id);
                    profile.viewedSeriesIds.add(doc.id);
                });
            } else {
                console.warn("[Rec] Likes fetch failed (Permissions?):", likesResult.reason);
            }

            // [3] Process Ratings
            const ratingsResult = results[2];
            if (ratingsResult.status === 'fulfilled') {
                ratingsResult.value.forEach(doc => {
                    const data = doc.data();
                    if (data.rating >= 3) {
                        ratedSeries.push({ id: doc.id, rating: data.rating });
                        profile.viewedSeriesIds.add(doc.id);
                    }
                });
            } else {
                console.warn("[Rec] Ratings fetch failed (Permissions?):", ratingsResult.reason);
            }

            // [4] Process Comments
            const commentsResult = results[3];
            if (commentsResult.status === 'fulfilled') {
                commentsResult.value.forEach(doc => {
                    const data = doc.data();
                    if (data.seriesId) {
                        commentedSeriesIds.push(data.seriesId);
                        profile.viewedSeriesIds.add(data.seriesId);
                    }
                });
            } else {
                // Ignore failure (index might be missing initially, though userId single index usually exists)
                // console.warn("[Rec] Comments fetch failed:", commentsResult.reason);
            }

            // 5. Resolve Tags
            // Combine all unique series IDs from interaction
            const targetIds = [...new Set([
                ...likedSeriesIds,
                ...ratedSeries.map(r => r.id),
                ...commentedSeriesIds // Include commented series
            ])];

            const recentTargets = targetIds.slice(0, 50);

            if (recentTargets.length > 0) {
                try {
                    const seriesObjects = await ContentService.getSeriesByIds(recentTargets);
                    seriesObjects.forEach(series => {
                        const isLiked = likedSeriesIds.includes(series.id);
                        const isCommented = commentedSeriesIds.includes(series.id); // [NEW]
                        const ratingObj = ratedSeries.find(r => r.id === series.id);
                        const ratingVal = ratingObj ? ratingObj.rating : 0;

                        let score = 0;
                        if (isLiked) score += 3;
                        if (isCommented) score += 4; // [NEW] Commenting is high engagement
                        if (ratingVal > 0) score += ratingVal;

                        if (series.tags && Array.isArray(series.tags)) {
                            series.tags.forEach(tag => {
                                profile.tagScores[tag] = (profile.tagScores[tag] || 0) + score;
                            });
                        }
                        if (series.authorId) {
                            profile.authorScores[series.authorId] = (profile.authorScores[series.authorId] || 0) + score;
                        }
                    });
                } catch (e) {
                    console.warn("[Rec] Series Detail Fetch Failed", e);
                }
            }

        } catch (globalError) {
            console.error("Critical Error in TasteProfile:", globalError);
            // Return whatever partial profile we have
        }

        console.timeEnd("TasteProfile");
        return profile;
    },

    getCandidatePool: async () => {
        try {
            // [CACHE STRATEGY]
            // Update Key to v3 to invalidate previous bad caches
            const cachedStr = sessionStorage.getItem('rec_candidate_pool_v3');
            if (cachedStr) {
                const cached = JSON.parse(cachedStr);
                if (Array.isArray(cached) && cached.length > 0) {
                    return cached;
                }
            }

            // 1. Try Standard Strategies (May fail if Index missing)
            const [trending, newArrivals] = await Promise.all([
                ContentService.getTrendingSeries(50).catch(e => {
                    console.warn("[Rec] Trending fetch failed (Index?):", e);
                    return [];
                }),
                ContentService.getNewArrivals(50).catch(e => {
                    console.warn("[Rec] NewArrivals fetch failed (Index?):", e);
                    return [];
                })
            ]);

            const poolMap = new Map();
            [...trending, ...newArrivals].forEach(s => {
                if (s && s.id) poolMap.set(s.id, s);
            });

            let pool = Array.from(poolMap.values());

            // 2. [DESPERATION FALLBACK] 
            // If pool is empty (likely due to missing indexes), try a simple query.
            // Simple: where('status', '==', 'published') + limit(20). No OrderBy.
            if (pool.length === 0) {
                console.warn("[Rec] Standard strategies failed. Using Desperation Fallback (No-Sort).");
                try {
                    const simpleQ = query(
                        collection(db, 'series'),
                        where('status', '==', 'published'),
                        limit(20)
                    );
                    const snap = await getDocs(simpleQ);
                    const fallbackItems = snap.docs.map(ContentService._normalizeSeries);
                    pool = fallbackItems;
                } catch (e) {
                    console.error("[Rec] Desperation Fallback failed:", e);
                }
            }

            // Only cache if we actually found something
            if (pool.length > 0) {
                sessionStorage.setItem('rec_candidate_pool_v3', JSON.stringify(pool));
            }

            return pool;
        } catch (e) {
            console.error("[Rec] Pool Fetch logic failed:", e);
            return [];
        }
    },

    getEmptyShelfPicks: async (userId) => {
        const pool = await RecommendationService.getCandidatePool();
        // Return fallback if pool is empty
        if (!pool || pool.length === 0) {
            const result = [];
            result.isFallback = true;
            return result;
        }

        let scoredCandidates = [];

        // Try to calculate personal taste
        let profile = null;
        if (userId) {
            profile = await RecommendationService.calculateTasteProfile(userId);
        }

        if (profile) {
            scoredCandidates = pool.map(series => {
                // Filter viewed
                if (profile.viewedSeriesIds.has(series.id)) return null;

                let score = 0;
                // Tag Resonance
                if (series.tags) {
                    series.tags.forEach(tag => {
                        if (profile.tagScores[tag]) {
                            score += profile.tagScores[tag];
                        }
                    });
                }
                // Author Affinity
                if (profile.authorScores[series.authorId]) {
                    score += (profile.authorScores[series.authorId] * 2);
                }
                return { series, score };
            }).filter(item => item !== null && item.score > 0);

            scoredCandidates.sort((a, b) => b.score - a.score);
        }

        // [FALLBACK STRATEGY]
        let isFallback = false;

        if (scoredCandidates.length < 5) {
            console.log("[Rec] Not enough personal matches. Filling with trending fallback.");
            isFallback = true;

            const viewed = profile ? profile.viewedSeriesIds : new Set();

            // Get items from pool that are NOT in scoredCandidates AND NOT viewed
            const existingIds = new Set(scoredCandidates.map(i => i.series.id));

            let fallback = pool.filter(s =>
                !existingIds.has(s.id) &&
                !viewed.has(s.id)
            );

            // [FIX] If strict filtering results in too few items, IGNORE viewed history
            // Better to show a read book than an empty slot.
            if (fallback.length + scoredCandidates.length < 5) {
                // console.log("[Rec] Extending pool with previously viewed items to ensure fullness.");
                fallback = pool.filter(s => !existingIds.has(s.id));
            }

            // Fill up to 5
            const needed = 5 - scoredCandidates.length;
            const fill = fallback.slice(0, needed).map(s => ({ series: s, score: 0 }));

            scoredCandidates = [...scoredCandidates, ...fill];
        }

        // [HYBRID FIX] 
        // Return Array to satisfy legacy checks (if (!picks || picks.length)), 
        // but attach property for new UI logic.
        const result = scoredCandidates.map(item => item.series);
        result.isFallback = isFallback;
        return result;
    },

    /**
     * Sync User's Taste Profile to Firestore (Server-side)
     * Used for "Target Push" and "Strategy Dashboard"
     */
    syncUserTaste: async (userId) => {
        if (!userId) return;

        try {
            // 1. Calculate Profile
            const profile = await RecommendationService.calculateTasteProfile(userId);
            if (!profile || !profile.tagScores) return;

            // 2. Extract Top 5 Tags
            const sortedTags = Object.entries(profile.tagScores)
                .sort((a, b) => b[1] - a[1]) // Descending
                .slice(0, 5)
                .map(item => item[0]);

            if (sortedTags.length === 0) return;

            // 3. Write to Firestore (stats.taste)
            const { doc, updateDoc, setDoc } = await import("firebase/firestore");
            const userRef = doc(db, "users", userId);

            // Nested field update 'stats.tasteTags'
            await updateDoc(userRef, {
                'stats.tasteTags': sortedTags,
                'stats.lastTasteSync': new Date().toISOString()
            }).catch(async (e) => {
                // Fallback if 'stats' map doesn't exist
                console.warn("[Rec] Sync update failed, trying setMerge:", e);
                await setDoc(userRef, {
                    stats: {
                        tasteTags: sortedTags,
                        lastTasteSync: new Date().toISOString()
                    }
                }, { merge: true });
            });

            console.log("[Rec] Taste Profile Synced:", sortedTags);

        } catch (e) {
            console.error("[Rec] Sync Failed:", e);
        }
    }
};
